import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { hubApi } from '../../lib/hubApi';
import { AdminActionMenu, type AdminActionKind } from '../../components/shared/AdminActionMenu';
import { HubPageHeader } from '../../components/hub/HubPageHeader';

type Holder = {
  userId: string;
  userName: string;
  fullName: string;
  isActive: boolean;
  isFrozen: boolean;
  lastLoginAt: number | null;
  hasMfa: boolean;
  // 'permission_set_group' is reserved for a future enhancement: distinguishing
  // grants that come via PermissionSetGroup membership from direct perm-set grants.
  // Today both surface as 'permission_set'.
  grantedVia: ('profile' | 'permission_set' | 'permission_set_group')[];
  permissionSetIds: string[];
};

// Mirrors server RiskPermission shape from permissionsRisk.service.ts.
// Keep tier literals in sync if the catalogue evolves.
type RiskPermission = {
  catalogue: {
    permissionApiName: string;
    displayName: string;
    description: string;
    tier: 'red' | 'orange' | 'yellow';
  };
  holderCount: number;
  inactiveHolderCount: number;
  holders: Holder[];
};

const TIER_BAR: Record<RiskPermission['catalogue']['tier'], string> = {
  red: 'bg-sunset',
  orange: 'bg-sunrise',
  yellow: 'bg-sunrise/70',
};

type Tab = 'risk' | 'permission' | 'user';

export function HubUsersPermissionsPage() {
  const [tab, setTab] = useState<Tab>('risk');
  const [data, setData] = useState<{ permissions: RiskPermission[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await hubApi.get<{ permissions: RiskPermission[]; totalUsers: number }>(
        '/permissions/risk',
      );
      setData({ permissions: r.data.permissions });
    } catch (e) {
      const m = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      setError(m ?? (e as Error).message ?? 'Failed to load permissions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (data) return;
    void load();
  }, [data, load]);

  return (
    <section className="space-y-6 max-w-5xl">
      <HubPageHeader
        title="Users & Permissions"
        subtitle="Risk-tiered permissions and the people who hold them."
      />

      {error && (
        <div className="px-4 py-3 rounded-lg border border-danger/40 bg-danger/10 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="flex gap-1 border-b border-border-subtle">
        <TabHeader active={tab === 'risk'} onClick={() => setTab('risk')}>
          Risk-first
        </TabHeader>
        <TabHeader active={tab === 'permission'} onClick={() => setTab('permission')}>
          Permission-first
        </TabHeader>
        <TabHeader active={tab === 'user'} onClick={() => setTab('user')}>
          User-first
        </TabHeader>
      </div>

      {tab === 'risk' && (
        <RiskFirstTab
          loading={loading}
          permissions={data?.permissions ?? []}
          expanded={expanded}
          setExpanded={setExpanded}
          onActionComplete={load}
        />
      )}
      {tab === 'permission' && <PermissionFirstTab permissions={data?.permissions ?? []} />}
      {tab === 'user' && <UserFirstTab permissions={data?.permissions ?? []} />}
    </section>
  );
}

function TabHeader({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 -mb-px border-b-2 text-sm transition-colors ${
        active
          ? 'border-orange text-text-strong font-medium'
          : 'border-transparent text-text-muted hover:text-text'
      }`}
    >
      {children}
    </button>
  );
}

function RiskFirstTab({
  loading,
  permissions,
  expanded,
  setExpanded,
  onActionComplete,
}: {
  loading: boolean;
  permissions: RiskPermission[];
  expanded: string | null;
  setExpanded: (v: string | null) => void;
  onActionComplete: () => void;
}) {
  if (loading) return <p className="text-text-muted text-sm">Loading permissions…</p>;
  if (permissions.length === 0)
    return <p className="text-text-muted text-sm">No risk-tier permissions found.</p>;

  return (
    <div className="flex flex-col gap-2">
      {permissions.map((p) => {
        const isOpen = expanded === p.catalogue.permissionApiName;
        return (
          <div
            key={p.catalogue.permissionApiName}
            className="rounded-lg border border-border-subtle bg-surface-2 shadow-card overflow-hidden transition-all hover:border-border-strong"
          >
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setExpanded(isOpen ? null : p.catalogue.permissionApiName)}
              className="w-full flex items-stretch gap-3 px-4 py-3 text-left hover:bg-surface-hover transition-colors"
            >
              <span className={`block w-[3px] self-stretch rounded-sm ${TIER_BAR[p.catalogue.tier]}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-text-strong">{p.catalogue.displayName}</div>
                <div className="text-xs text-text-muted mt-1">{p.catalogue.description}</div>
              </div>
              <div className="text-xs text-right">
                <div className="text-text-strong">
                  <strong className="text-base">{p.holderCount}</strong>{' '}
                  <span className="text-text-muted">users</span>
                </div>
                {p.inactiveHolderCount > 0 && (
                  <div className="text-text-muted mt-1">{p.inactiveHolderCount} inactive 90+ days</div>
                )}
              </div>
              <div className="self-center text-text-subtle">
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-border-subtle px-4 py-3 bg-surface-1/60">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle mb-2">
                  Holders
                </div>
                {p.holders.length === 0 ? (
                  <p className="text-xs text-text-muted">No active users hold this permission.</p>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {p.holders.map((h) => (
                      <HolderRow
                        key={h.userId}
                        holder={h}
                        permissionSetIds={h.permissionSetIds}
                        onActionComplete={onActionComplete}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function HolderRow({
  holder,
  permissionSetIds,
  onActionComplete,
}: {
  holder: Holder;
  permissionSetIds: string[];
  onActionComplete: () => void;
}) {
  const lastLogin =
    holder.lastLoginAt !== null
      ? new Date(holder.lastLoginAt).toLocaleDateString()
      : 'never';
  const viaProfile = holder.grantedVia.includes('profile');
  // The freeze/unfreeze action swaps based on current state — showing
  // "Freeze user" on an already-frozen account would just fail server-side
  // and confuse the admin. Other actions stay the same.
  const freezeAction: AdminActionKind = holder.isFrozen ? 'unfreeze_user' : 'freeze_user';
  // Profile-granted holders: no Unassign (you can't unassign a profile from here;
  // route through BPX Success per the design rule). Perm-set-granted holders get
  // Unassign first since it's the targeted fix.
  const actions: AdminActionKind[] = viaProfile
    ? [freezeAction, 'end_all_sessions', 'reset_mfa']
    : ['unassign_permission_set', freezeAction, 'end_all_sessions'];

  return (
    <div
      className={`flex items-center gap-3 text-xs px-3 py-2 rounded-md border transition-colors ${
        holder.isFrozen
          ? 'bg-warning/10 border-warning/40'
          : 'bg-surface-2 border-border-subtle'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium text-text-strong flex items-center gap-2">
          <span className="truncate">{holder.fullName}</span>
          {holder.isFrozen && (
            <span
              className="text-[9px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-warning/20 border border-warning/40 text-warning shrink-0"
              title="This user's login is currently frozen."
            >
              Frozen
            </span>
          )}
        </div>
        <div className="text-text-muted mt-0.5 truncate">
          {holder.userName} · {holder.grantedVia.join(' + ')} · last login {lastLogin}
          {!holder.hasMfa && <> · <span className="text-danger font-medium">no MFA</span></>}
        </div>
      </div>
      <AdminActionMenu
        targetUserId={holder.userId}
        targetUserLabel={holder.fullName}
        actions={actions}
        permissionSetId={permissionSetIds[0]}
        onComplete={onActionComplete}
      />
    </div>
  );
}

function PermissionFirstTab({ permissions }: { permissions: RiskPermission[] }) {
  const [q, setQ] = useState('');
  const filtered = permissions.filter(
    (p) =>
      p.catalogue.displayName.toLowerCase().includes(q.toLowerCase()) ||
      p.catalogue.permissionApiName.toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-2 shadow-card p-4">
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search permissions…"
        aria-label="Search permissions"
        className="mb-4 px-3 py-2 rounded-md border border-border bg-surface-1 text-sm w-72 text-text placeholder:text-text-subtle focus:outline-none focus:border-border-strong"
      />
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
            <th className="text-left py-2 pr-3">Permission</th>
            <th className="text-right py-2 px-3">Holders</th>
            <th className="text-right py-2 pl-3">Inactive 90d+</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((p) => (
            <tr key={p.catalogue.permissionApiName} className="border-t border-border-subtle">
              <td className="py-2.5 pr-3 text-text">{p.catalogue.displayName}</td>
              <td className="text-right px-3 text-text tabular-nums">{p.holderCount}</td>
              <td className="text-right pl-3 text-text-muted tabular-nums">{p.inactiveHolderCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserFirstTab({ permissions }: { permissions: RiskPermission[] }) {
  type Row = { userId: string; fullName: string; userName: string; perms: string[] };
  const rows = new Map<string, Row>();
  for (const p of permissions) {
    for (const h of p.holders) {
      const existing = rows.get(h.userId) ?? {
        userId: h.userId,
        fullName: h.fullName,
        userName: h.userName,
        perms: [],
      };
      existing.perms.push(p.catalogue.displayName);
      rows.set(h.userId, existing);
    }
  }
  const sorted = Array.from(rows.values()).sort((a, b) => b.perms.length - a.perms.length);

  return (
    <div className="flex flex-col gap-2">
      {sorted.map((r) => (
        <div
          key={r.userId}
          className="text-sm px-4 py-3 rounded-lg border border-border-subtle bg-surface-2 shadow-card flex items-center gap-3 transition-all hover:bg-surface-hover hover:border-border-strong"
        >
          <div className="flex-1 min-w-0">
            <div className="font-medium text-text-strong truncate">{r.fullName}</div>
            <div className="text-xs text-text-muted mt-0.5 truncate">{r.userName}</div>
          </div>
          <div className="text-xs text-right max-w-xs">
            <div className="text-text">
              <strong className="text-text-strong">{r.perms.length}</strong> risk perms
            </div>
            <div className="text-text-muted mt-0.5">
              {r.perms.slice(0, 3).join(', ')}
              {r.perms.length > 3 ? '…' : ''}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
